import { useCallback, useEffect, useRef, useState } from 'react';
import { audioPath } from 'constants/audioConstants';
import { Decimal } from 'decimal.js';
import useSound from 'hooks/useSound';
import { useTableSelector } from 'hooks/useTableSelector';
import useGameService from 'services/GameService';
import { convertToNumber, currencyFormatter, toLocaleStringNumber } from 'utils/functions';

import ActionButton from './ActionButton';
import RaiseActionExpanded from './RaiseActionExpanded';

interface RaiseProps {
  disabled: boolean;
  /** See PokerActions' submitAction - carries the re-send closure and the action name. */
  submitAction: (send: (stamp: string) => void, name: string) => void;
  /** The emit was not acked in time (or could not be sent). PokerActions decides what happens. */
  onAckTimeout: () => void;
  triggerHideServerTimeout: () => void;
}

const RaiseAction = ({
  disabled,
  submitAction,
  onAckTimeout,
  triggerHideServerTimeout
}: RaiseProps) => {
  const { move } = useTableSelector((table) => table.game);
  const player = useTableSelector((table) => table.players[+move]);
  const playersBetDisplay = player?.betDisplay ?? 0;
  const tableType = useTableSelector((table) => table.game.tabletype);
  const [toggleRaise, setToggleRaise] = useState(false);
  const [lastAction, setLastAction] = useState('');
  const { raise } = useGameService();
  const [amount, setAmount] = useState<any>(null);
  // The bet/raise tap sound, exactly where the old sprite `notify` lived: the
  // tap that OPENS the tray and the tap that CONFIRMS both go through
  // showPanel, so one call there covers both (owner, 8/27 — confirm-only was
  // a regression). The confirm ALSO reaches the dispatch effect below via the
  // tray's own bet button (onBet={hidePanel}), so both sites carry the same
  // per-play dedupeKey: within one confirm the second call is collapsed, and
  // the open tap seconds earlier is long outside the window.
  // `userGesture` because in grid view the tap that opens the tray can also be
  // the tap that focuses the cell, and the cell's focus handler sits OUTSIDE
  // this button — so it runs after. Without the flag that first tap is judged
  // against a table that is not focused yet and silently dropped, which is
  // exactly how it behaved (owner, 8/27). See hooks/useSound.
  const [playPlacingBet] = useSound(audioPath.placingBet, { userGesture: true });
  const cueBetTap = () => playPlacingBet({ dedupeKey: 'placing-bet' });
  const [isDisableRaiseButton, setIsDisableRaiseButton] = useState(false);
  const raiseButtonRef = useRef<HTMLButtonElement>(null);
  // Wraps the Bet/Raise button AND its expanded panel. Used to tell an
  // "inside" pointer-down (button or panel — keep current behavior) from an
  // "outside" one (felt, other controls — dismiss without betting).
  const raiseAreaRef = useRef<HTMLDivElement>(null);
  const { min, playersStack, isAllIn, hasPotLimit, fullPot } = useTableSelector(
    (table) => table.gameState.pokerActions.betMultiplier
  );
  const highestBetPlayer = useTableSelector((table) => table.game.highestBetPlayer);
  const highestPlayerLBet = convertToNumber(highestBetPlayer?.betDisplay);
  // What GameService.raise() will put on the wire, by the same rule it uses. The
  // name handed to submitAction must match the action the ENGINE records, or
  // pendingActionOutcome can never say `landed` (PR review finding 6).
  const emittedName = highestPlayerLBet === 0 ? 'bet' : 'raise';

  useEffect(() => {
    if (
      (lastAction !== 'RAISE_SLIDER' && (toggleRaise || lastAction === 'ALL_IN')) ||
      lastAction === undefined ||
      lastAction === ''
    ) {
      return;
    }
    if (toggleRaise === false && (amount >= min || +amount === playersStack)) {
      const raiseAmount = Decimal.sum(amount, playersBetDisplay);
      // The tap cue sits OUTSIDE the send closure on purpose: submitAction
      // re-invokes that closure on a re-send, and this sound belongs to the
      // player's press, not to a timer-driven retry. useSound's `userGesture`
      // flag makes the same distinction.
      cueBetTap();
      submitAction((stamp) => raise(stamp, raiseAmount, triggerHideServerTimeout, onAckTimeout), emittedName);
      setLastAction('');
    }

    setIsDisableRaiseButton(
      (amount < min && amount !== playersStack) || amount > playersStack || amount === '.'
    );
  }, [toggleRaise, amount, lastAction, min, raise]);

  const showPanel = () => {
    if (isAllIn) {
      cueBetTap();
      submitAction(
        (stamp) =>
          raise(
            stamp,
            Decimal.sum(playersStack, playersBetDisplay),
            triggerHideServerTimeout,
            onAckTimeout
          ),
        // This path calls raise(), NOT allIn(), so the wire carries bet/raise -
        // naming it 'allin' made every raise-panel all-in read as `unknown`. The
        // engine may still convert an all-in-sized bet to ACTION.ALLIN, which
        // pendingActionOutcome then reports as `unknown`: the honest answer
        // rather than a wrong one.
        emittedName
      );
    } else {
      cueBetTap();
      setToggleRaise(!toggleRaise);
      if (lastAction !== 'RAISE_SLIDER') {
        if (toggleRaise) setLastAction('RAISE_SLIDER');
        setAmount(min);
      }
    }
  };

  const hidePanel = (lastAction: string) => {
    setLastAction(lastAction);
    setToggleRaise(false);
  };

  // Close the panel WITHOUT committing a bet (used for click-away). The commit
  // useEffect only fires when toggleRaise drops to false AND lastAction is a
  // real action (RAISE_SLIDER / ALL_IN); resetting lastAction to '' makes its
  // guard return early, so closing this way bets nothing. The player must press
  // Bet/Raise again to reopen — and pressing it while open still commits as
  // before, since that path goes through showPanel, not here.
  const dismissPanel = useCallback(() => {
    setToggleRaise(false);
    setLastAction('');
    setAmount(null);
    // Discarding the amount must also clear any "invalid amount" disable: the
    // commit/disable useEffect early-returns once lastAction is '', so it won't
    // recompute this for us — leaving Bet/Raise stuck disabled for the rest of
    // the turn after dismissing an over-stack amount. Reset it explicitly here.
    setIsDisableRaiseButton(false);
  }, []);

  // While the panel is open, a pointer-down outside the raise area dismisses it.
  // raiseAreaRef wraps both the Bet/Raise button and the panel, so taps on
  // either are "inside" and keep their existing behavior (commit / adjust);
  // only taps elsewhere (the felt, other action buttons) close it.
  useEffect(() => {
    if (!toggleRaise || isAllIn) return;
    const handlePointerDown = (event: PointerEvent) => {
      const el = raiseAreaRef.current;
      if (el && !el.contains(event.target as Node)) {
        dismissPanel();
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [toggleRaise, isAllIn, dismissPanel]);

  const formatAmount = (val: number) => {
    return tableType !== 's' ? toLocaleStringNumber(convertToNumber(val)) : currencyFormatter(val);
  };

  let label: string;
  if (toggleRaise) {
    const displayAmount = amount > playersStack ? playersStack : amount;
    const adjusted = hasPotLimit && amount > fullPot ? fullPot : displayAmount;
    label =
      highestPlayerLBet === 0 ? `Bet ${formatAmount(adjusted)}` : `Raise ${formatAmount(adjusted)}`;
  } else if (highestPlayerLBet === 0) {
    label = 'Bet';
  } else if (isAllIn) {
    label = `All In ${formatAmount(playersStack)}`;
  } else {
    label = 'Raise';
  }

  return (
    // display:contents — generates no box, so the flex layout of the action row
    // and the panel's absolute positioning (relative to .poker-actions) are
    // unchanged; the wrapper exists only to give raiseAreaRef a DOM boundary
    // spanning the button + panel for the click-away check.
    <div ref={raiseAreaRef} style={{ display: 'contents' }}>
      <ActionButton
        variant={isAllIn ? 'allin' : 'raise'}
        icon={isAllIn ? 'allin' : 'raise'}
        onClick={showPanel}
        disabled={isDisableRaiseButton || disabled}
        // First click (panel closed, not all-in) only opens the slider — no
        // commit yet, so keep the player's turn timer visible. The second
        // click (panel open) closes the panel which triggers the raise
        // commit via the useEffect below, and all-in mode commits on click.
        committing={isAllIn || toggleRaise}>
        {label}
      </ActionButton>
      {toggleRaise && !isAllIn && (
        <RaiseActionExpanded
          onBet={hidePanel}
          setAmount={(val: string) => {
            setLastAction('RAISE_SLIDER');
            setAmount(val);
          }}
          amount={amount}
          raiseButtonRef={raiseButtonRef}
        />
      )}
    </div>
  );
};

export default RaiseAction;
